/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { Target } from '@fluentui/react/lib/Callout';
import { IContextualMenuItem } from '@fluentui/react/lib/ContextualMenu';
import { observer } from 'mobx-react';
import React from 'react';
import { defineMessages } from 'react-intl';
import { EntityDropdown } from 'src/core/components/EntityDropdown';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { EntityStore } from 'src/core/types/EntityStore';
import { substringSearchStrategy } from 'src/core/utils/searchStrategies/substringSearchStrategy';
import { EntityLabel } from 'src/customText/verticals/extractionLabeler/types';
import { getNotFoundItem } from 'src/luis/modules/labeling/utils/entityDropdown/getNotFoundItem';
import { LuisId } from 'src/luis/types/Resource';
import { Label } from 'src/modules/labeling/types/Label';
import { Selection } from 'src/modules/labeling/types/Selection';

export type ExtractionEntityDropdownData = { target: Target; selection: Selection; cancelSelectionCB?: () => void };

export type ExtractionEntityDropdownProps = {
    schema: EntityStore[];
    data: ExtractionEntityDropdownData;
    labels: Label[];
    onAddLabel: (label: Omit<EntityLabel, 'entity'> & { entityId: string }) => void;
    onRemoveLabel: (label: Omit<EntityLabel, 'entity'> & { entityId: string }) => void;
    onDismiss: () => void;
    onCreateNewEntity: (name: string) => void;
};

const messages = defineMessages({
    createNewEntity: {
        id: 'extractionLabeler.ExtractionEntityDropdown.createNewEntity',
        defaultMessage: 'Create new entity'
    },
    noEntities: {
        id: 'extractionLabeler.ExtractionEntityDropdown.noEntities',
        defaultMessage: 'No entities found'
    },
    unlabel: {
        id: 'extractionLabeler.ExtractionEntityDropdown.unlabel',
        defaultMessage: 'Remove label'
    }
});

export const ExtractionEntityDropdown = observer((props: ExtractionEntityDropdownProps) => {
    const { data, onAddLabel, onRemoveLabel, schema, labels, onDismiss, onCreateNewEntity } = props;
    const [query, setQuery] = React.useState('');
    const { localize } = useLocalization();

    const { target, selection, cancelSelectionCB } = data;
    const selectedLabels = labels.filter(label => label.start === selection[0] && label.end === selection[1]);

    const labeledMap = new Map<LuisId, Label>();
    selectedLabels.forEach(label => {
        labeledMap.set(label.entity.id, label);
    });

    const handleCreateNewEntity = () => {
        onCreateNewEntity(query);
        setQuery('');
    };

    let items = schema.map<IContextualMenuItem>(
        entity =>
            entity.prebuilts.length === 0 && {
                key: entity.id,
                text: entity.name,
                iconProps: labeledMap.has(entity.id) ? { iconName: 'CheckMark' } : undefined,
                onClick: () => {
                    if (labeledMap.has(entity.id)) {
                        const label = labeledMap.get(entity.id);
                        onRemoveLabel({ startCharIndex: label.start, endCharIndex: label.end, entityId: label.entity.id.toString() });
                    } else {
                        onAddLabel({ entityId: entity.id, startCharIndex: selection[0], endCharIndex: selection[1] });
                    }
                    cancelSelectionCB?.();
                }
            }
    );

    items = substringSearchStrategy(items, query, 'text');

    const notFoundItem: IContextualMenuItem = getNotFoundItem({
        text: query,
        onClick: e => {
            e.preventDefault();
            handleCreateNewEntity();
        },
        secondaryText: localize(messages.createNewEntity)
    });

    if (items.length === 0) {
        if (query === '') {
            items.push({
                key: 'noEntities',
                secondaryText: localize(messages.noEntities)
            });
        } else {
            items.push(notFoundItem);
        }
    }

    const footerActionableItems: IContextualMenuItem[] = [];
    const labeledItems = items.filter(item => item.iconProps?.iconName === 'CheckMark');
    if (labeledItems.length) {
        footerActionableItems.push({
            key: 'unlabel',
            text: localize(messages.unlabel),
            iconProps: { iconName: 'RevToggleKey' },
            onClick: () => {
                labeledItems.forEach(item => {
                    const label = labeledMap.get(item.key);
                    onRemoveLabel({ startCharIndex: label.start, endCharIndex: label.end, entityId: label.entity.id.toString() });
                    cancelSelectionCB?.();
                });
            }
        });
    }

    return (
        <EntityDropdown
            query={query}
            items={items}
            target={target}
            onDismiss={onDismiss}
            onChangeQuery={setQuery}
            onSubmit={handleCreateNewEntity}
            footerActionableItems={footerActionableItems}
        />
    );
});
